import {
  type KK,
  type DefineDevice,
  type SetMagnetSenior,
  KIO,
  Record,
  OutputMode,
  VirtualHE3SEWireless,
  VirtualHE3SEWireless_24g,
  VirtualMars68ProWireless,
  VirtualMars68ProWireless_24g,
  VirtualMars68Wireless,
  VirtualMg68Max_24g,
  VirtualMg68Max,
  VirtualMars68Wireless_24g,
  VirtualHe6Ultra,
  VirtualHe6Ultra_24g,
  VirtualMG68ACE_W,
  VirtualMG68ACE_W_24g,
  VirtualIrokFe87V2IMC,
  VirtualIrokFe87V2IMC_24g,
  VirtualIrokFe87V2,
  VirtualIrokFe87V2_24g,
  VK,
} from '../define/define'
import type { DataSetsList, DataSetLink } from '../define/response'
import type { PostString, PostInt, PostCfgRename, PostImportSetting, PostCfgBindShortCut } from '../define/request'
import { deviceManager } from '../device/device_manager'
import { DataDeadZone, DataTravel, KeysGroup, PostSetLed, DataProfileForDecorate, PostDynamicColor } from '@libs/types/response'
import { createProfile } from '@hid/define/define_profile'
import { SpecificItem } from '@libs/types/enum'

export interface MouseProfile {
  DpiColors: number[][]
  DpiIndex: number // 表示当前 DPI 索引
  DpiList: number[] // 表示 DPI 列表，最大长度为7
  Rate: number // 表示鼠标报告率
}

export interface DataProfile {
  LightLastIsCustom: boolean
  Keys: KIO[]
  Macros: Map<number, Record[]>
  LightMode: PostSetLed
  ColorsCustom: Map<KK, number>
  ColorsDynamic: PostDynamicColor
  Mouse: MouseProfile
  MagnetData: Map<number, SetMagnetSenior>
  MagnetTravel: Map<KK, DataTravel>
  //KeysGroup: Map<KK, KeyGroup>;
  KeysGroup: KeysGroup[]
  DeadZone: number // 废弃
  DeadZone1: [number, number] // 废弃
  DeadZoneList: Map<KK, DataDeadZone> //只有切换配置的时候, 会使用
  Items: Map<SpecificItem, number>
  Decorates?: DataProfileForDecorate[] // 灯光装饰颜色配置
}
export interface ProfileMetadata {
  Name: string // 配置名称
  Shortcut?: VK[] // 绑定的快捷键列表
  ShortcutRet?: boolean // 快捷键注册状态
  ProfilesMap: Map<DefineDevice, DataProfile> // 设备配置映射
}

export interface ProfileList {
  CurrentProfileIndex: number // 当前配置索引
  Profiles: ProfileMetadata[] // 配置列表
}
// 转换 Map 为普通对象
export function mapToObject(map: Map<any, any>): { [key: string]: any } {
  const obj: { [key: string]: any } = {}
  if (map instanceof Map) {
    map.forEach((value, key) => {
      obj[key] = value
    })
  } else {
    console.error('Input is not a Map object.')
  }
  return obj
}

// 转换普通对象为 Map
function objectToMap(obj: { [key: string]: any }): Map<any, any> {
  const map = new Map()
  if (typeof obj === 'object' && obj !== null) {
    for (const key in obj) {
      if (!isNaN(Number(key))) {
        const k = Number(key)
        map.set(k, obj[k])
      } else {
        map.set(key, obj[key])
      }
    }
  } else {
    console.error('Input is not a plain object.')
  }
  return map
}

interface DeviceMap {
  wired: DefineDevice
  wireless: DefineDevice
}

// 示例设备映射
const deviceMapping: DeviceMap[] = [
  { wired: VirtualHE3SEWireless, wireless: VirtualHE3SEWireless_24g },
  { wired: VirtualMars68ProWireless, wireless: VirtualMars68ProWireless_24g },
  { wired: VirtualMars68Wireless, wireless: VirtualMars68Wireless_24g },
  { wired: VirtualMg68Max, wireless: VirtualMg68Max_24g },
  { wired: VirtualHe6Ultra, wireless: VirtualHe6Ultra_24g },
  { wired: VirtualMG68ACE_W, wireless: VirtualMG68ACE_W_24g },
  { wired: VirtualIrokFe87V2IMC, wireless: VirtualIrokFe87V2IMC_24g },
  { wired: VirtualIrokFe87V2, wireless: VirtualIrokFe87V2_24g },
]

function getAnotherDevice(defineDevice: DefineDevice): DefineDevice | null {
  for (const device of deviceMapping) {
    if (device.wired === defineDevice) {
      return device.wireless
    } else if (device.wireless === defineDevice) {
      return device.wired
    }
  }
  return null
}
export class DataProfileManage {
  public _dataProfile: DataProfile = {} as DataProfile
  private profileList: ProfileList = {} as ProfileList
  private profileKey = 'dataProfileList'

  public get dataProfile() {
    const currentProfile = this.profileList.Profiles[this.profileList.CurrentProfileIndex]
    const profile = currentProfile.ProfilesMap.get(deviceManager.deviceID)
    if (profile) {
      this._dataProfile = profile
      return this._dataProfile
    } else {
      this.createDefaultProfile()
      return this._dataProfile
    }
    //throw new Error("dataProfile is undefined");
  }

  public set dataProfile(dataProfile: DataProfile) {
    this._dataProfile = dataProfile
  }

  constructor() {
    this.load()
  }
  // 存储 DataProfile 到 localStorage
  public save() {
    //处理一个设备对应两个配置
    const anotherDevice = getAnotherDevice(deviceManager.deviceID)
    if (anotherDevice) {
      this.profileList.Profiles[this.profileList.CurrentProfileIndex].ProfilesMap.set(anotherDevice, this.dataProfile)
    }
    const profiles = this.profileList.Profiles.map((profile) => {
      const profilesMapObj: { [key: string]: any } = {}
      profile.ProfilesMap.forEach((value, key) => {
        profilesMapObj[String(key)] = {
          ...value,
          Macros: value.Macros,
          ColorsCustom: value.ColorsCustom,
          MagnetData: value.MagnetData,
          MagnetTravel: value.MagnetTravel,
          DeadZoneList: value.DeadZoneList,
          Items: value.Items,
          Decorates: value.Decorates,
        }
      })
      return {
        Name: profile.Name,
        Shortcut: profile.Shortcut,
        ShortcutRet: profile.ShortcutRet,
        ProfilesMap: profilesMapObj,
      }
    })
    const profileListObj = {
      CurrentProfileIndex: this.profileList.CurrentProfileIndex,
      Profiles: profiles,
    }

    const profileString = JSON.stringify(profileListObj)
    // console.log("save", profileString)
    localStorage.setItem(this.profileKey, profileString)
  }

  // 从 localStorage 读取 DataProfile
  private load() {
    const profileString = localStorage.getItem(this.profileKey)
    if (!profileString) {
      this.createDefaultListAndProfile()
      return
    }
    const profileListObj = JSON.parse(profileString)
    this.profileList = {
      CurrentProfileIndex: profileListObj.CurrentProfileIndex,
      Profiles: profileListObj.Profiles.map((profile: { Name: string; Shortcut?: VK[]; ShortcutRet?: boolean; ProfilesMap: { [key: string]: any } }) => {
        const profilesMap = new Map<number, DataProfile>()
        for (const key in profile.ProfilesMap) {
          const value = profile.ProfilesMap[key]
          profilesMap.set(Number(key), {
            ...value,
            Macros: objectToMap(value.Macros),
            ColorsCustom: objectToMap(value.ColorsCustom),
            MagnetData: objectToMap(value.MagnetData),
            MagnetTravel: objectToMap(value.MagnetTravel),
            DeadZoneList: objectToMap(value.DeadZoneList),
            Items: objectToMap(value.Items),
            Decorates:
              value.Decorates && Array.isArray(value.Decorates)
                ? value.Decorates.map((decorate: any) => ({
                    ...decorate,
                    ColorsCustom: objectToMap(decorate.ColorsCustom),
                  }))
                : undefined,
          })
        }
        return {
          Name: profile.Name,
          Shortcut: profile.Shortcut || [],
          ShortcutRet: profile.ShortcutRet || false,
          ProfilesMap: profilesMap,
        }
      }),
    }

    if (this.profileList.CurrentProfileIndex >= this.profileList.Profiles.length) {
      throw new Error('Current profile index out of range')
    }
    const currentProfile = this.profileList.Profiles[this.profileList.CurrentProfileIndex]
    if (currentProfile.ProfilesMap.has(deviceManager.deviceID)) {
      this.dataProfile = currentProfile.ProfilesMap.get(deviceManager.deviceID) as DataProfile
    } else {
      this.createDefaultProfile()
    }
  }

  // 创建配置列表和当前设备的配置
  private createDefaultListAndProfile() {
    this.profileList = {
      CurrentProfileIndex: 0,
      Profiles: [{ Name: '默认配置', ProfilesMap: new Map<DefineDevice, DataProfile>() }],
    }
    this.createDefaultProfile()
  }

  // 创建当前设备的配置
  private createDefaultProfile() {
    this._dataProfile = createProfile(deviceManager.deviceID)
    this.profileList.Profiles[this.profileList.CurrentProfileIndex].ProfilesMap.set(deviceManager.deviceID, this._dataProfile)
    this.save()
  }

  // 新增配置构建返回值
  private buildCfgCommonReturn(): DataSetsList {
    const dataSetList: DataSetLink[] = this.profileList.Profiles.map((profile) => ({
      Name: profile.Name,
      File: ``,
      ShortcutRet: profile.ShortcutRet || false,
      Shortcut: profile.Shortcut || [],
    }))
    return {
      Current: this.profileList.CurrentProfileIndex,
      List: dataSetList,
    }
  }

  // 实现绑定快捷键的数据
  public CfgBindShortcut(post: PostCfgBindShortCut) {
    if (post.ID < 0 || post.ID >= this.profileList.Profiles.length) {
      throw new Error('index out of range')
    }
    this.profileList.Profiles[post.ID].Shortcut = post.Shortcut
    // 假设绑定即成功，或者根据实际需求处理状态
    this.profileList.Profiles[post.ID].ShortcutRet = post.Shortcut && post.Shortcut.length > 0
    this.save()
    return this.buildCfgCommonReturn()
  }

  // 新增配置
  public CfgCreate(post: PostString) {
    this.profileList.Profiles.push({ Name: post.Value, ProfilesMap: new Map<DefineDevice, DataProfile>() })
    this.save()
    return this.buildCfgCommonReturn()
  }

  //配置列表
  public CfgList() {
    return this.buildCfgCommonReturn()
  }

  //删除配置
  public CfgDelete(post: PostInt) {
    if (post.Value < 0 || post.Value >= this.profileList.Profiles.length) {
      throw new Error('index out of range')
    }
    if (this.profileList.CurrentProfileIndex === post.Value) {
      throw new Error('can not delete current profile')
    }
    if (this.profileList.Profiles.length === 1) {
      throw new Error('can not delete last profile')
    }
    this.profileList.Profiles.splice(post.Value, 1)
    if (this.profileList.CurrentProfileIndex > post.Value) {
      this.profileList.CurrentProfileIndex--
    }
    this.save()

    return this.buildCfgCommonReturn()
  }

  //配置重命名
  public CfgRename(post: PostCfgRename) {
    if (post.ID < 0 || post.ID >= this.profileList.Profiles.length) {
      throw new Error('index out of range')
    }
    this.profileList.Profiles[post.ID].Name = post.Name
    this.save()

    return this.buildCfgCommonReturn()
  }

  //配置切换
  public CfgSwitch(post: PostInt) {
    if (post.Value < 0 || post.Value >= this.profileList.Profiles.length) {
      throw new Error('index out of range')
    }
    if (this.profileList.CurrentProfileIndex === post.Value) {
      return this.buildCfgCommonReturn()
    }
    this.profileList.CurrentProfileIndex = post.Value
    const dataProfile = this.profileList.Profiles[post.Value].ProfilesMap.get(deviceManager.deviceID)
    if (dataProfile) {
      this.dataProfile = dataProfile
      this.save()
    } else {
      this.createDefaultProfile()
    }
    return this.buildCfgCommonReturn()
  }

  public ResetData() {
    this.createDefaultProfile()
  }

  public ResetKeys: KIO[] = []
  public ResetKmeKeys: KIO[] = []

  public clearKmeKeys() {
    this.ResetKmeKeys = []
  }

  public clearResetKeys() {
    if (this.ResetKeys.length < 1) {
      return
    }
    this.ResetKeys.forEach((value) => {
      if (value.Output.Mode == OutputMode.Macro) {
        this.dataProfile.Macros.delete(value.Output.DataIndex)
      }
      if (value.Output.Mode == OutputMode.Magnet) {
        this.dataProfile.MagnetData.delete(value.Output.DataIndex)
      }
      this.clearDataProfileKey(value)
    })
    this.ResetKeys = []
    this.save()
  }

  private clearDataProfileKey(value: KIO) {
    this.dataProfile.Keys = this.dataProfile.Keys.filter((value1) => value1.Input.KK != value.Input.KK || (value1.Input.KK == value.Input.KK && value.Input.WithFN != value1.Input.WithFN))
  }

  //导入配置
  public CfgImport(post: PostImportSetting) {
    const jsonString = atob(post.DataBase64)
    const value: DataProfile = JSON.parse(jsonString)
    const dp = {
      ...value,
      DeadZoneList: objectToMap(value.DeadZoneList),
      Items: objectToMap(value.Items),
      MagnetTravel: objectToMap(value.MagnetTravel),
      Macros: objectToMap(value.Macros),
      ColorsCustom: objectToMap(value.ColorsCustom),
      MagnetData: objectToMap(value.MagnetData),
      Decorates: value.Decorates
        ? value.Decorates.map((decorate: any) => ({
            ...decorate,
            ColorsCustom: objectToMap(decorate.ColorsCustom),
          }))
        : undefined,
    }
    const profileMetadata: ProfileMetadata = {
      Name: post.Name,
      ProfilesMap: new Map<DefineDevice, DataProfile>([[deviceManager.deviceID, dp]]),
    }
    this.profileList.Profiles.push(profileMetadata)
    this.save()

    return this.buildCfgCommonReturn()
  }

  // 导出配置
  CfgExport(post: PostInt): string {
    if (post.Value < 0 || post.Value >= this.profileList.Profiles.length) {
      throw new Error('index out of range')
    }
    const dataProfile = this.profileList.Profiles[post.Value].ProfilesMap.get(deviceManager.deviceID)
    return btoa(JSON.stringify(dataProfile))
  }

  changeDpi(index: number) {
    this.dataProfile.Mouse.DpiIndex = index
    this.save()
  }

  changeMouseReportRate(value: number) {
    this.dataProfile.Mouse.Rate = value
    this.save()
  }
}

let _dataProfileManage: DataProfileManage | null = null

export const dataProfileManage = () => {
  if (_dataProfileManage == null) {
    _dataProfileManage = new DataProfileManage()
  }
  return _dataProfileManage
}
